/**
 * ful-legend.js — Single source of truth for all Fulkit economics.
 * If it's not here, it's not official.
 *
 * Works on both client and server (pure data, no "use client" needed).
 *
 * FIXED = user-facing contract (changes require announcement).
 * VARIABLE = internal ops (adjustable as costs change).
 */

// === FIXED (user-facing contract) ========================================

export const FUL_EXCHANGE = {
  fulPerDollar: 100,        // 100 Ful = $1. Always.
  dollarPerFul: 0.01,
};

export const PLANS = {
  trial: {
    label: "Trial",
    price: 0,
    priceLabel: "Free",
    durationDays: 14,
    fulTotal: 150,           // total, not per month
    fulPerMonth: 150,        // compat alias (trial is one-shot)
    maxTokens: 2048,
    model: "claude-sonnet-4-6",
    compressAt: 80000,
    integrations: 1,
    vaultNotes: 10,
    spendCap: 2.50,
  },
  standard: {
    label: "Standard",
    priceMonthly: 9,
    priceAnnual: 90,
    price: 9,                // compat alias (monthly)
    priceLabel: "$9/mo",
    fulPerMonth: 450,
    maxTokens: 2048,
    model: "claude-sonnet-4-6",
    compressAt: 80000,
    integrations: Infinity,
    vaultNotes: Infinity,
    spendCap: null,          // derived from getSpendCap()
  },
  pro: {
    label: "Pro",
    priceMonthly: 15,
    priceAnnual: 150,
    price: 15,
    priceLabel: "$15/mo",
    fulPerMonth: 800,
    maxTokens: 4096,
    model: "claude-sonnet-4-6",
    compressAt: 80000,
    integrations: Infinity,
    vaultNotes: Infinity,
    spendCap: null,
  },
  byok: {
    label: "BYOK",
    price: 0,
    priceLabel: "Free",
    fulPerMonth: Infinity,
    maxTokens: 128000,
    model: "claude-opus-4-6",
    compressAt: 180000,
    support: "self-service",
    platformFee: 0,
  },
  founder: {
    label: "Founder",
    price: 0,
    priceLabel: "Free",
    fulPerMonth: Infinity,
    maxTokens: 2048,
    model: "claude-sonnet-4-6",
    compressAt: 80000,
    integrations: Infinity,
    vaultNotes: Infinity,
  },
  guest: {
    label: "Guest",
    price: 0,
    priceLabel: "Free",
    fulPerMonth: 450,
    maxTokens: 2048,
    model: "claude-sonnet-4-6",
    compressAt: 80000,
    integrations: Infinity,
    vaultNotes: Infinity,
  },
  owner: {
    label: "Owner",
    price: 0,
    priceLabel: "Owner",
    fulPerMonth: Infinity,
    maxTokens: 128000,
    model: "claude-opus-4-6",
    compressAt: 180000,
  },
};

// Manual one-off top-up. Complementary to AUTO_REFILL — manual
// serves casual / subscription-averse users; auto-refill serves
// established usage. Both priced at 50 Ful/$1 (2× markup on the
// FUL_EXCHANGE internal rate).
export const CREDITS = {
  price: 2,
  fulAmount: 100,
  priceLabel: "$2",
  description: "$2 per 100 messages",
  stripeLookup: "fulkit_credits_100",
};

export const AUTO_REFILL = {
  defaultAmount: 250,           // Fül per refill
  defaultPrice: 5,              // USD per refill
  defaultThreshold: 50,         // Fül remaining triggers refill
  cooldownMs: 3600000,          // 1 hour between refills
  globalMonthlyCap: 5000,       // Fül — hard ceiling per month
  stripeLookup: "fulkit_credits_250",
};

export const REFERRALS = {
  fulPerRefPerMonth: 100,
  dollarPerRefPerMonth: 1,
  fulPerDollar: FUL_EXCHANGE.fulPerDollar,   // alias for referral math
  creditPerRef: 1,                            // compat
  tiers: [
    { id: 1, min: 1,   max: 6,        fulPerRef: 100, label: "Piece" },
    { id: 2, min: 7,   max: 14,       fulPerRef: 100, label: "Component" },
    { id: 3, min: 15,  max: 24,       fulPerRef: 100, label: "Tool" },
    { id: 4, min: 25,  max: 99,       fulPerRef: 110, label: "Builder" },
    { id: 5, min: 100, max: 249,      fulPerRef: 120, label: "Architect" },
    { id: 6, min: 250, max: Infinity, fulPerRef: 130, label: "Ambassador" },
  ],
  freeStandardThreshold: 9,
  freeProThreshold: 15,
  freeAtStandard: 9,   // compat alias
  freeAtPro: 15,        // compat alias
  payoutMinimum: 10,
  payoutTierMin: 4,
  payoutMinTier: 4,     // compat alias
  payoutMinUsd: 10,     // compat alias
};

// === VARIABLE (internal ops) =============================================

export const COST_BASIS = {
  targetCostPerFul: 0.012,    // target with caching + lean tools
  currentCostPerFul: 0.015,   // actual Sonnet cost today (was 0.09 when running Opus for all)
  // Compat aliases
  targetCostPerMsg: 0.012,
  currentCostPerMsg: 0.015,
  lastUpdated: "2026-04-01",
};

// === METERED FEATURE COSTS ===============================================
// House markup on Ful spending. Same rate applies to image gen,
// future video gen, voice gen, etc. Adjusting this is a one-line
// change to house policy.
export const MARKUP = 1.35;

// Per-feature raw provider costs (USD). Image generation is the first
// metered feature using this pattern. Each provider's raw cost × MARKUP
// ÷ FUL_EXCHANGE.dollarPerFul = Ful cost per image (ceil-rounded).
//
// Provider cost references (Apr 2026):
//   - replicate-flux-schnell: Replicate's budget tier, very cheap
//   - openai: gpt-image-1 medium quality, 1024x1024
//   - replicate-flux-dev: Black Forest Flux Dev, mid tier
//   - replicate-nano-banana-2: Google Gemini Flash Image v2 (rough
//                              estimate; refine after a few invoices)
//
// When md/auto-refill.md unrolls, image gen plugs into the per-surface
// cap system as the first metered feature.
export const IMAGE_GEN_RAW_COSTS = {
  "replicate-flux-schnell": 0.003,
  "openai": 0.020,
  "replicate-flux-dev": 0.025,
  "replicate-nano-banana-2": 0.050,
};

// Tier compositions — five UX tiers users see as pills, each resolving
// to a single provider (or all four). Hybrid naming: descriptive label
// plus model name in parens for users who want to know the engine.
// "iterate" is dynamic (handled outside tiers — pulls 4× of whichever
// provider the user picked from a prior generation).
export const IMAGE_GEN_TIERS = {
  fast:     { label: "Fast (Schnell)",       providers: ["replicate-flux-schnell"] },
  gpt:      { label: "GPT",                  providers: ["openai"] },
  enhanced: { label: "Enhanced (Flux Dev)",  providers: ["replicate-flux-dev"] },
  banana:   { label: "Banana",               providers: ["replicate-nano-banana-2"] },
  all4:     { label: "All 4",                providers: ["replicate-flux-schnell", "openai", "replicate-flux-dev", "replicate-nano-banana-2"] },
};

// Single-image Fül cost for a provider. Ceil-rounded so we never
// undercharge against the markup target.
//
// markupOverride (optional) — server-side callers can pass current MARKUP
// resolved from owner_settings via lib/get-markup.js getMarkup(). Defaults
// to the static MARKUP constant for client-side / non-async contexts.
// See md/specs/economic-engine.md §4 + Cluster D3 execution doc.
export function costForProvider(provider, markupOverride) {
  const raw = IMAGE_GEN_RAW_COSTS[provider];
  if (raw == null) throw new Error(`Unknown image-gen provider: ${provider}`);
  const effectiveMarkup = (typeof markupOverride === "number" && markupOverride > 0) ? markupOverride : MARKUP;
  return Math.ceil((raw * effectiveMarkup) / FUL_EXCHANGE.dollarPerFul);
}

// Total Fül cost for a tier. For "iterate," fromProvider must be set
// (the provider the user picked from a prior generation, fired 4×).
//
// markupOverride (optional) — same semantics as costForProvider above.
export function costForTier(tier, fromProvider, markupOverride) {
  if (tier === "iterate") {
    if (!fromProvider) throw new Error("costForTier(iterate, ...) needs fromProvider");
    return costForProvider(fromProvider, markupOverride) * 4;
  }
  const t = IMAGE_GEN_TIERS[tier];
  if (!t) throw new Error(`Unknown image-gen tier: ${tier}`);
  return t.providers.reduce((sum, p) => sum + costForProvider(p, markupOverride), 0);
}

// Back-compat alias for any caller still importing the flat constant.
// Existing chat tool / api route move to costForProvider() during this
// tranche — once those are migrated this can be deleted.
export const IMAGE_GEN_FUL_COST = costForProvider("openai");

export function getSpendCap(plan) {
  if (!PLANS[plan]) return null;
  if (PLANS[plan].spendCap !== undefined && PLANS[plan].spendCap !== null)
    return PLANS[plan].spendCap;
  const fulLimit = PLANS[plan].fulPerMonth;
  if (!isFinite(fulLimit)) return null;
  // Use MAX of current and target cost so cap never fires before Ful limit
  const costPerFul = Math.max(COST_BASIS.currentCostPerFul, COST_BASIS.targetCostPerFul);
  // 1.5x buffer above expected cost
  return Math.ceil(fulLimit * costPerFul * 1.5 * 100) / 100;
  // At current costs (Sonnet $0.015): Standard = 450 x 0.015 x 1.5 = $10.13, Pro = 800 x 0.015 x 1.5 = $18.00
  // Post-optimization ($0.012): Standard = $8.10, Pro = $14.40
}

export const CIRCUIT_BREAKER = {
  yellowPct: 0.50,            // 50% of MRR -> alert owner
  redPct: 0.60,               // 60% of MRR -> throttle responses
  throttledMaxTokens: 1024,
};

export const HOT_SEATS = {
  total: 5,
  minWeekly: 1,
  minMonthly: 4,
  graceDays: 30,
};

// === BILLING STATE MACHINE ===============================================

export const BILLING_STATES = {
  NORMAL: { maxPercent: 89 },
  SOFT_WARNING: { percent: 90, showOnce: true },
  HEADS_UP: { percent: 98, showOnce: true },
  WRAP_UP: { percent: 99, reserveLastFul: true },
  LIMIT: { percent: 100, disableInput: true, keepHistoryAccessible: true },
};

// === PROJECTION DEFAULTS =================================================

export const PROJECTIONS = {
  standardProSplit: 0.70,        // 70% Standard, 30% Pro assumption
  freeSeatsDefault: 6,           // referral-earned free plans assumption
  avgMsgsPerUserPerMonth: 300,   // blended average for API cost projection
  hostingBase: 25,               // base monthly hosting (Vercel + Supabase + Redis)
  hostingPerHundredUsers: 10,    // incremental hosting per 100 users
  blendedRefCreditPerUser: 1,    // ~$1/mo blended referral credit per paying user
};

// === TELEMETRY ===========================================================

export const TELEMETRY = {
  trackCostPerMessage: true,
  trackOutputTokensPerTier: true,
  trackToolCallsPerMessage: true,
  trackHabitEngineAccuracy: true,
};
